import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useReducer,
} from "react";
import PropTypes from "prop-types";
import { Collapse, Box, Typography } from "@mui/material";
import EMPActionCard from "../../shared/components/EMPActionCard/EMPActionCard";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import MotionPlus from "../../shared/Icons/MotionPlus";
import MotionMinus from "../../shared/Icons/MotionMinus";
import { useQueryClient } from "@tanstack/react-query";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import TimeOfSaleReport from "../../shared/components/EMPModal/ModalFragments/TimeOfSaleReport";
import EVPowerPromise from "../../shared/components/EMPModal/ModalFragments/EVPowerPromise";
import { doesAttestationExist } from "../../shared/services/localstorage/LocalStorageService";
import {
  ACCEPT_REJECT_STATUS_CREATED,
  ACCEPT_REJECT_STATUS_ACCEPTED,
  ACCEPT_REJECT_STATUS_CANCELLED,
  PURCHASE_DEPOSIT_STATUS_NOT_SCHEDULED,
  PURCHASE_DEPOSIT_STATUS_AUTO_ACCEPTED,
  PURCHASE_DEPOSIT_STATUS_CREATED,
  PURCHASE_DEPOSIT_STATUS_REFUND,
  PURCHASE_DEPOSIT_STATUS_REJECTED,
  TRADE_IN_STATUS_NOT_SCHEDULED,
  TRADE_IN_STATUS_EVALUATE,
  TRADE_IN_STATUS_COMPLETED,
  TIME_OF_SALE_ATTEST,
  TIME_OF_SALE_COMPLETED,
  EV_POWER_PROMISE_ATTEST,
  EV_POWER_PROMISE_COMPLETED,
  ACCEPT_REJECT_STATUS_CANCELED,
  PURCHASE_DEPOSIT_STATUS_ACCEPTED,
  ROUTEONE_STATUS_CREATED,
} from "../../shared/constants/ActionCardConstants";
import TradeInDecision from "../../shared/components/EMPModal/ModalFragments/TradeInDecision";
import EMPBlankSection from "../../shared/components/EMPBlankSection/EMPBlankSection";
import { getPurchaseRequestReasonLabel } from "../../shared/constants/CancellationReasonConstants";
import { useTranslation } from "react-i18next";
import { trackOrderDetailsPageAction } from "../../shared/services/analytics/OrderDetailsPageAnalytics";
import { trackReferralExit } from "../../shared/services/analytics/AnalyticsService";
import {
  isRouteOne,
  getRouteOneStatus,
  setCanceledPurchaseRequestDisplay,
  setPurchaseDepositRefundCard,
} from "../../shared/rules/ActionCardRules";
import RefundPurchaseDeposit from "../../shared/components/EMPModal/ModalFragments/RefundPurchaseDeposit";
import PurchaseRequestDecision from "../../shared/components/EMPModal/ModalFragments/PurchaseRequestDecision";
import PurchaseDepositDecision from "../../shared/components/EMPModal/ModalFragments/PurchaseDepositDecision";
import { PURCHASE_DEPOSIT_STATUS } from "../../common/Constants";
import { UserContext } from "../../shared/context/userContext";
import { TRADE_IN_STATUS } from "../../shared/constants/TradeInConstants";
import { usePurchaseDeposit } from "../../shared/hooks/usePurchaseDeposit";
import { useCanAutoAcceptDeposit } from "../../shared/hooks/useCanAutoAcceptDeposit";
import { actionCardReducer } from "../../shared/reducers/actionCardReducer";

const PURCHASE_REQUEST_CARD_IDS = [
  ACCEPT_REJECT_STATUS_CREATED,
  ACCEPT_REJECT_STATUS_ACCEPTED,
  ACCEPT_REJECT_STATUS_CANCELED,
  ACCEPT_REJECT_STATUS_CANCELLED,
];

const TRADE_IN_CARD_IDS = [
  TRADE_IN_STATUS_NOT_SCHEDULED,
  TRADE_IN_STATUS_EVALUATE,
  TRADE_IN_STATUS_COMPLETED,
];

// Lincoln shows these as banners or in the Financing and Trade-In tabs, so the
// old tiles are dropped from the grid. The canceled-order refund tile stays.
const LINCOLN_HIDDEN_CARD_IDS = [
  ...PURCHASE_REQUEST_CARD_IDS,
  ...TRADE_IN_CARD_IDS,
  PURCHASE_DEPOSIT_STATUS_NOT_SCHEDULED,
  PURCHASE_DEPOSIT_STATUS_AUTO_ACCEPTED,
  PURCHASE_DEPOSIT_STATUS_CREATED,
  PURCHASE_DEPOSIT_STATUS_ACCEPTED,
  PURCHASE_DEPOSIT_STATUS_REJECTED,
];

const formatDate = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  return new Date(dateValue).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const getHeightBasedOnViewport = () => {
  const viewportWidth = window.innerWidth;

  if (viewportWidth < 768) {
    return "300px";
  }

  if (viewportWidth < 1024) {
    return "280px";
  }

  if (viewportWidth < 1440) {
    return "260px";
  }

  return "240px";
};

const isTradeInStatusValid = (customerReservation, tradeIn) => {
  const tradeInOptIn = customerReservation?.dealerTradeInOptIn;
  const validStatuses = [
    TRADE_IN_STATUS.ACCEPT_KEEP,
    TRADE_IN_STATUS.ACCEPT_AUCTION,
    TRADE_IN_STATUS.REJECTED,
  ];

  return Boolean(
    tradeIn && tradeInOptIn && validStatuses.includes(tradeIn.status)
  );
};

const hasValidDepositAmount = (deposit) =>
  deposit && deposit.depositAmount !== null;

const getPurchaseDepositStatus = ({
  canAutoAcceptDeposit,
  customerReservation,
  purchaseDeposit,
}) => {
  const purchaseInfoStatus =
    customerReservation.purchaseInfo?.acceptRejectStatus;
  const deposit = customerReservation.purchaseDeposit;
  const depositStatus = deposit?.acceptRejectStatus;
  const refundStatus = deposit?.refundStatus;

  if (canAutoAcceptDeposit) {
    return "auto-accept";
  }

  if (
    purchaseInfoStatus !== "accepted" &&
    purchaseInfoStatus !== "canceled" &&
    deposit &&
    depositStatus === null &&
    hasValidDepositAmount(deposit)
  ) {
    return "not-scheduled";
  }

  if (
    depositStatus === PURCHASE_DEPOSIT_STATUS.ACCEPTED &&
    refundStatus === null &&
    purchaseInfoStatus !== "canceled"
  ) {
    return "accepted";
  }

  if (
    purchaseInfoStatus === "accepted" &&
    deposit &&
    hasValidDepositAmount(deposit) &&
    refundStatus !== PURCHASE_DEPOSIT_STATUS.REFUNDED &&
    ![
      PURCHASE_DEPOSIT_STATUS.DECLINED,
      PURCHASE_DEPOSIT_STATUS.REJECTED,
    ].includes(depositStatus)
  ) {
    return "created";
  }

  if (deposit && depositStatus === PURCHASE_DEPOSIT_STATUS.DECLINED) {
    return null;
  }

  if (deposit && depositStatus === PURCHASE_DEPOSIT_STATUS.REJECTED) {
    return "rejected";
  }

  return setPurchaseDepositRefundCard(
    purchaseDeposit,
    customerReservation.purchaseInfo
  );
};

const getTradeInStatus = ({ customerReservation, tradeIn }) => {
  const purchaseInfoStatus =
    customerReservation.purchaseInfo?.acceptRejectStatus;
  const validTradeInStatus = isTradeInStatusValid(customerReservation, tradeIn);

  if (tradeIn === null) {
    return null;
  }

  if (!validTradeInStatus && purchaseInfoStatus !== "accepted") {
    return "not-scheduled";
  }

  if (!validTradeInStatus && purchaseInfoStatus === "accepted") {
    return "evaluate";
  }

  if (validTradeInStatus) {
    return "completed";
  }

  return null;
};

const getTimeOfSaleReportStatus = ({ customerReservation, orderId }) => {
  if (!customerReservation?.isFederalTaxCreditEligible) {
    return null;
  }

  if (doesAttestationExist(orderId, "time-of-sale")) {
    return "completed";
  }

  return "attest";
};

const getEvPowerPromiseStatus = ({ customerReservation, orderId }) => {
  const isModelEJourney =
    customerReservation?.journeyType === "Model-E" ||
    customerReservation?.journeyType === "Model-e";

  if (!isModelEJourney) {
    return null;
  }

  if (doesAttestationExist(orderId, "ev-power-promise")) {
    return "completed";
  }

  return "attest";
};

const getAttestationDate = ({ attestationData, orderId, attestationType }) => {
  if (!doesAttestationExist(orderId, attestationType) || !attestationData) {
    return null;
  }

  const parsedAttestationData = JSON.parse(attestationData);

  return parsedAttestationData?.[orderId]?.[attestationType]?.date || null;
};

const getStatuses = ({
  customerReservation,
  tradeIn,
  canAutoAcceptDeposit,
  purchaseDeposit,
  orderId,
  attestationData,
}) => [
  {
    acceptRejectStatus:
      customerReservation.purchaseInfo?.acceptRejectStatus || null,
    actionDate: formatDate(
      customerReservation.purchaseInfo?.purchaseRequestDealerAction
        ?.dealerActionDate
    ),
    actionUserId:
      customerReservation.purchaseInfo?.purchaseRequestDealerAction
        ?.dealerActionUserId || null,
    cancellationReason: getPurchaseRequestReasonLabel(
      customerReservation.purchaseInfo?.purchaseRequestDealerAction
        ?.dealerRejectedReason
    ),
  },
  {
    purchaseDepositStatus: getPurchaseDepositStatus({
      canAutoAcceptDeposit,
      customerReservation,
      purchaseDeposit,
    }),
    purchaseDepositDate: formatDate(
      customerReservation.purchaseDeposit?.acceptRejectDate
    ),
  },
  {
    tradeInStatus: getTradeInStatus({ customerReservation, tradeIn }),
    tradeInDate: formatDate(tradeIn?.actionDateNA),
  },
  {
    timeOfSaleReport: getTimeOfSaleReportStatus({
      customerReservation,
      orderId,
    }),
    timeOfSaleReportDate: getAttestationDate({
      attestationData,
      orderId,
      attestationType: "time-of-sale",
    }),
  },
  {
    evPowerPromise: getEvPowerPromiseStatus({
      customerReservation,
      orderId,
    }),
    evPowerPromiseDate: getAttestationDate({
      attestationData,
      orderId,
      attestationType: "ev-power-promise",
    }),
  },
  {
    routeOneStatus: getRouteOneStatus(customerReservation),
  },
];

const getActionCardDirectives = (t) => [
  {
    id: ACCEPT_REJECT_STATUS_CREATED,
    status: "action-required",
    title: t("common.actions.purchaseRequest"),
    text: t("common.actions.reviewPurchaseRequest"),
    hasActionButton: true,
    buttonType: "contained",
    buttonText: t("common.buttonCtas.openAction", { action: "Request" }),
  },
  {
    id: ACCEPT_REJECT_STATUS_ACCEPTED,
    status: "action-completed",
    title: t("common.actions.purchaseRequest"),
    text: t("common.actions.acceptedPurchaseRequest"),
    hasActionButton: true,
    buttonText: t("common.buttonCtas.seeDetails"),
    buttonType: "outlined",
    completedDate: null,
    user: null,
  },
  {
    id: ACCEPT_REJECT_STATUS_CANCELED,
    status: "action-completed",
    title: t("common.actions.purchaseRequest"),
    text: t("common.actions.canceledPurchaseRequest"),
    hasActionButton: false,
    completedDate: null,
    user: null,
    cancellationReason: null,
  },
  {
    id: ACCEPT_REJECT_STATUS_CANCELLED,
    status: "action-completed",
    title: t("common.actions.purchaseRequest"),
    text: t("common.actions.canceledPurchaseRequest"),
    hasActionButton: false,
    completedDate: null,
    user: null,
    cancellationReason: null,
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_NOT_SCHEDULED,
    status: "action-not-available",
    title: t("common.actions.purchaseDeposit"),
    text: t("common.actions.unavailablePurchaseDeposit"),
    hasActionButton: true,
    buttonType: "outlined",
    buttonText: "View Deposit Info",
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_CREATED,
    status: "action-required",
    title: t("common.actions.purchaseDeposit"),
    text: t("common.actions.reviewPurchaseDeposit"),
    hasActionButton: true,
    buttonType: "contained",
    buttonText: t("common.buttonCtas.openAction", { action: "Deposit" }),
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_ACCEPTED,
    status: "action-completed",
    title: t("common.actions.purchaseDeposit"),
    text: t("common.actions.acceptedPurchaseDeposit"),
    hasActionButton: true,
    buttonType: "outlined",
    buttonText: t("common.buttonCtas.seeDetails"),
    completedDate: null,
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_REJECTED,
    status: "action-completed",
    title: t("common.actions.purchaseDeposit"),
    text: t("common.actions.rejectedPurchaseDeposit"),
    hasActionButton: true,
    buttonType: "outlined",
    buttonText: t("common.buttonCtas.seeDetails"),
    completedDate: null,
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_AUTO_ACCEPTED,
    status: "action-not-available",
    title: t("common.actions.purchaseDeposit"),
    text: t("common.actions.autoAcceptedPurchaseDeposit"),
    hasActionButton: true,
    buttonType: "outlined",
    buttonText: t("common.buttonCtas.seeDetails"),
  },
  {
    id: PURCHASE_DEPOSIT_STATUS_REFUND,
    status: "action-required",
    title: "Refund Purchase Deposit",
    text: "This Purchase Request has been canceled.  Please refund the purchase deposit.",
    hasActionButton: true,
    buttonType: "contained",
    buttonText: "Refund Deposit",
  },
  {
    id: TRADE_IN_STATUS_NOT_SCHEDULED,
    status: "action-not-available",
    title: t("common.actions.tradeIn"),
    text: "You can evaluate the trade-in after accepting the Purchase Request.",
    hasActionButton: true,
    buttonText: "See Trade-In Details",
    buttonType: "outlined",
  },
  {
    id: TRADE_IN_STATUS_EVALUATE,
    status: "action-required",
    title: t("common.actions.tradeIn"),
    text: t("common.actions.evaluateTradeIn"),
    hasActionButton: true,
    buttonText: t("common.buttonCtas.evaluateTradeIn"),
    buttonType: "contained",
  },
  {
    id: TRADE_IN_STATUS_COMPLETED,
    status: "action-completed",
    title: t("common.actions.tradeIn"),
    text: t("common.actions.evaluatedTradeIn"),
    hasActionButton: true,
    buttonText: t("common.buttonCtas.seeDetails"),
    buttonType: "outlined",
    completedDate: null,
  },
  {
    id: TIME_OF_SALE_ATTEST,
    status: "action-required",
    title: t("common.actions.timeOfSale"),
    text: "You must submit a time of sale report on the IRS website.",
    hasActionButton: true,
    buttonText: t("common.buttonCtas.noteAsCompleted"),
    buttonType: "contained",
  },
  {
    id: TIME_OF_SALE_COMPLETED,
    status: "action-completed",
    title: t("common.actions.timeOfSale"),
    text: t("common.actions.completedTimeOfSale"),
    hasActionButton: false,
    buttonText: "",
    completedDate: null,
  },
  {
    id: EV_POWER_PROMISE_ATTEST,
    status: "action-required",
    title: t("common.actions.powerPromise"),
    text: "You must enroll your customer via SmartVincent.",
    hasActionButton: true,
    buttonText: t("common.buttonCtas.noteAsCompleted"),
    buttonType: "contained",
  },
  {
    id: EV_POWER_PROMISE_COMPLETED,
    status: "action-completed",
    title: t("common.actions.powerPromise"),
    text: t("common.actions.completedPowerPromise"),
    hasActionButton: false,
    buttonText: "",
    completedDate: null,
  },
  {
    id: ROUTEONE_STATUS_CREATED,
    status: "created",
    title: t("common.buttonCtas.routeOne"),
    text: t("common.actions.routeOneMessage"),
    hasActionButton: false,
    buttonText: "",
    completedDate: null,
  },
];

const ActionCardsRow = ({
  cards,
  getHandleClickFunction,
  orderId,
  isLincolnBrand,
}) => (
  <Box
    display="grid"
    gridTemplateColumns="repeat(12, 1fr)"
    gap={4}
    sx={{ mb: 4 }}
  >
    {cards.map((card) => (
      <Box
        key={card.id}
        gridColumn={{
          xs: "span 6",
          sm: "span 6",
          md: "span 4",
          lg: "span 4",
        }}
        id="action-card"
        data-testid="action-card"
      >
        <EMPActionCard
          status={card.status}
          hasActionButton={card.hasActionButton}
          title={card.title}
          text={card.text}
          buttonText={card.buttonText}
          buttonType={card.buttonType}
          onClick={getHandleClickFunction(card.id, orderId)}
          completedDate={card.completedDate}
          testId={card.id}
          isLincolnOrderDetails={isLincolnBrand}
        />
      </Box>
    ))}
  </Box>
);

ActionCardsRow.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  getHandleClickFunction: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isLincolnBrand: PropTypes.bool.isRequired,
};

const EmptyActionsSection = ({ message }) => (
  <Box
    display="grid"
    gridTemplateColumns="repeat(12, 1fr)"
    gap={4}
    sx={{
      mb: 4,
      background: "linear-gradient(135deg, #F5F6F7 0%, #FFFFFF 100%)",
      py: 8,
    }}
  >
    <Box gridColumn="span 4" sx={{ gridColumnStart: 5, gridColumnEnd: 9 }}>
      <Typography variant="body2" align="center">
        {message}
      </Typography>
    </Box>
  </Box>
);

EmptyActionsSection.propTypes = {
  message: PropTypes.string.isRequired,
};

// Lincoln shows purchase request, deposit and trade-in notices as banners.
const ActionBanner = ({ card, onClick, t, testId }) => {
  const isNewRequest = card.id === ACCEPT_REJECT_STATUS_CREATED;
  const completedText = card.completedDate
    ? ` ${t("common.actionStatus.completedOn")} ${card.completedDate}`
    : "";

  return (
    <Box sx={{ mb: 4 }} data-testid={testId}>
      <EMPActionCard
        status={card.status}
        hasActionButton={card.hasActionButton}
        title={
          isNewRequest ? t("common.actions.newPurchaseRequest") : card.title
        }
        text={
          isNewRequest
            ? t("common.actions.reviewRequestAndTakeAction")
            : `${card.text}${completedText}`
        }
        buttonText={
          isNewRequest ? t("common.buttonCtas.reviewRequest") : card.buttonText
        }
        buttonType={card.buttonType}
        onClick={onClick}
        testId={card.id}
        presentation="banner"
        isLincolnOrderDetails
      />
    </Box>
  );
};

ActionBanner.propTypes = {
  card: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  t: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
};

const ActionCardGrid = ({ orderId, handleTabChange }) => {
  const [showSecondRow, setShowSecondRow] = useState(false);
  const [collapsedSize, setCollapsedSize] = useState("240px");
  const boxRef = useRef(null);
  const collapseRef = useRef(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const isLincolnBrand = user?.brand === "L";
  const purchaseDeposit = usePurchaseDeposit(orderId);
  const canAutoAcceptDeposit = useCanAutoAcceptDeposit(orderId);
  const actionCardDirectives = getActionCardDirectives(t);

  const [state, dispatch] = useReducer(actionCardReducer, actionCardDirectives);
  const orderData = queryClient.getQueryData(["order", { orderId }]);
  const { customerReservation, tradeIn } = orderData || {};
  const attestationData = sessionStorage.getItem("attestation");
  const { handleModal } = useContext(ModalContext);
  const { modelYear, nameplate } = customerReservation || {};

  const toggleSecondRow = () => {
    const actionName = showSecondRow
      ? "hide all action items"
      : "show all action items";

    setShowSecondRow((prev) => !prev);

    trackOrderDetailsPageAction(
      null,
      actionName,
      orderId,
      modelYear,
      nameplate,
      user
    );
  };

  const updateCollapsedSize = () => {
    if (boxRef.current) {
      const height = boxRef.current.clientHeight + 16;
      setCollapsedSize(`${height}px`);
    }
  };

  useEffect(() => {
    updateCollapsedSize();

    const resizeObserver = new ResizeObserver(() => {
      updateCollapsedSize();
    });

    if (boxRef.current) {
      resizeObserver.observe(boxRef.current);
    }

    window.addEventListener("resize", updateCollapsedSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateCollapsedSize);
    };
  }, [state]);

  useEffect(() => {
    if (showSecondRow || !collapseRef.current) {
      return;
    }

    collapseRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [showSecondRow]);

  const getHandleClickFunction = (status, currentOrderId) => {
    switch (status) {
      case ACCEPT_REJECT_STATUS_ACCEPTED: {
        return () => {
          handleTabChange(2, "completed:purchase request accepted:see details");
        };
      }

      case ACCEPT_REJECT_STATUS_CREATED: {
        return () => {
          handleModal(
            t("common.actions.purchaseRequest"),
            <PurchaseRequestDecision orderId={currentOrderId} />
          );
        };
      }

      case PURCHASE_DEPOSIT_STATUS_NOT_SCHEDULED:
      case PURCHASE_DEPOSIT_STATUS_AUTO_ACCEPTED: {
        return () => {
          handleTabChange(
            3,
            "not available yet:purchase deposit:view deposit info"
          );
        };
      }

      case PURCHASE_DEPOSIT_STATUS_CREATED: {
        return () => {
          handleModal(
            t("common.actions.purchaseDeposit"),
            <PurchaseDepositDecision orderId={currentOrderId} />
          );
        };
      }

      case PURCHASE_DEPOSIT_STATUS_REFUND: {
        return () => {
          handleModal(
            t("common.buttonCtas.refundPurchaseDeposit"),
            <RefundPurchaseDeposit orderId={currentOrderId} />
          );
        };
      }

      case PURCHASE_DEPOSIT_STATUS_ACCEPTED: {
        return () => {
          handleTabChange(3, "completed:purchase deposit accepted:see details");
        };
      }

      case PURCHASE_DEPOSIT_STATUS_REJECTED: {
        return () => {
          handleTabChange(3, "completed:purchase deposit rejected:see details");
        };
      }

      case TRADE_IN_STATUS_NOT_SCHEDULED: {
        return () => {
          handleTabChange(4, "not available yet:trade in:see details");
        };
      }

      case TRADE_IN_STATUS_EVALUATE: {
        return () => {
          handleModal(
            t("common.actions.confirmTradeInDetails"),
            <TradeInDecision orderId={currentOrderId} />
          );
          trackOrderDetailsPageAction(
            null,
            "review trade in",
            currentOrderId,
            modelYear,
            nameplate,
            user
          );
        };
      }

      case TRADE_IN_STATUS_COMPLETED: {
        return () => {
          handleTabChange(4, "completed:trade in:see details");
        };
      }

      case TIME_OF_SALE_ATTEST: {
        return () => {
          handleModal(
            t("common.actions.timeOfSale"),
            <TimeOfSaleReport orderId={currentOrderId} />
          );
        };
      }

      case EV_POWER_PROMISE_ATTEST: {
        return () => {
          handleModal(
            t("common.actions.powerPromise"),
            <EVPowerPromise orderId={currentOrderId} />
          );
        };
      }

      default: {
        return () => handleModal("Default", "Default");
      }
    }
  };

  useEffect(() => {
    if (!customerReservation || canAutoAcceptDeposit === null) {
      return;
    }

    const statuses = getStatuses({
      customerReservation,
      tradeIn,
      canAutoAcceptDeposit,
      purchaseDeposit,
      orderId,
      attestationData,
    });

    dispatch({ type: "SET_ACTION_CARDS", payload: { statuses } });
    dispatch({ type: "UPDATE_COMPLETED_DATE", payload: statuses });
    dispatch({ type: "CHECK_FOR_CANCEL" });
    dispatch({
      type: "CHECK_FOR_ROUTEONE",
      payload: { statuses },
    });
    dispatch({ type: "SORT_ACTION_CARDS" });
  }, [
    customerReservation,
    tradeIn,
    canAutoAcceptDeposit,
    purchaseDeposit,
    orderId,
    attestationData,
  ]);

  const purchaseDepositBanner = isLincolnBrand &&
    customerReservation?.purchaseDeposit?.acceptRejectStatus === null && {
      card: {
        id: "purchase-deposit-notice",
        title: t("common.actions.purchaseDeposit"),
        text: t("common.actions.openPurchaseDepositMessage"),
        hasActionButton: false,
      },
      testId: "purchase-deposit-banner",
    };

  const renderBanner = ({ card, testId }) => (
    <ActionBanner
      key={card.id}
      card={card}
      onClick={
        card.hasActionButton
          ? getHandleClickFunction(card.id, orderId)
          : undefined
      }
      t={t}
      testId={testId}
    />
  );

  const emptyActions = isLincolnBrand ? null : (
    <EmptyActionsSection message={t("common.actionStatus.noActionsRequired")} />
  );

  if (state.length === 0) {
    return purchaseDepositBanner
      ? renderBanner(purchaseDepositBanner)
      : emptyActions;
  }

  if (isRouteOne(state)) {
    return (
      <>
        {purchaseDepositBanner && renderBanner(purchaseDepositBanner)}
        <EMPBlankSection
          message1={t("common.actions.routeOneMessage")}
          message2={
            <EMPButton
              href="https://www.routeone.net"
              target="_blank"
              rel="noopener noreferrer"
              buttonType="link"
              testId="route-one-button"
              onClick={() =>
                trackReferralExit("https://www.routeone.net", orderId, user)
              }
            >
              {t("common.buttonCtas.routeOne")}
            </EMPButton>
          }
        />
      </>
    );
  }

  if (setCanceledPurchaseRequestDisplay(state)) {
    return (
      <>
        {purchaseDepositBanner && renderBanner(purchaseDepositBanner)}
        <EMPBlankSection
          message1={t("common.actions.canceledPurchaseRequestMessage", {
            user: state[0].user,
            completedDate: state[0].completedDate,
            interpolation: { escapeValue: false },
          })}
          message2={t("common.actions.dynamicCancellationReason", {
            cancellationReason: state[0].cancellationReason,
            interpolation: { escapeValue: false },
          })}
        />
      </>
    );
  }

  const findBannerCard = (ids) =>
    isLincolnBrand ? state.find((card) => ids.includes(card.id)) : undefined;
  // Lincoln shows an accepted or canceled request as a chip in the header,
  // so only a new request gets a banner
  const purchaseRequestCard = findBannerCard([ACCEPT_REJECT_STATUS_CREATED]);
  const tradeInCard = findBannerCard(TRADE_IN_CARD_IDS);
  const purchaseRequestBanner = purchaseRequestCard && {
    card: purchaseRequestCard,
    testId: "purchase-request-banner",
  };
  const tradeInBanner = tradeInCard && {
    card: tradeInCard,
    testId: "trade-in-banner",
  };
  // Keep the new request first so it can be reviewed before other actions.
  const banners = [
    purchaseRequestBanner,
    purchaseDepositBanner,
    tradeInBanner,
  ].filter(Boolean);
  const cards = state.filter(
    (card) =>
      !banners.some((banner) => banner.card === card) &&
      !(isLincolnBrand && LINCOLN_HIDDEN_CARD_IDS.includes(card.id))
  );

  if (banners.length === 0 && cards.length === 0) {
    return emptyActions;
  }

  const collapseHeight =
    cards.length >= 3 ? getHeightBasedOnViewport() : "auto";
  const collapseOverflow = showSecondRow ? "scroll" : "hidden";
  const toggleIcon = showSecondRow ? <MotionMinus /> : <MotionPlus />;
  const toggleText = showSecondRow
    ? t("common.actionStatus.hideShow", { context: "hide" })
    : t("common.actionStatus.hideShow", { context: "show" });

  return (
    <>
      {banners.map(renderBanner)}
      {cards.length > 0 && (
        <Collapse
          in={showSecondRow}
          collapsedSize={collapsedSize}
          sx={{
            height: collapseHeight,
            overflow: collapseOverflow,
          }}
          ref={collapseRef}
          data-testid="action-card-collapse"
        >
          <Box ref={boxRef}>
            <ActionCardsRow
              cards={cards.slice(0, 3)}
              getHandleClickFunction={getHandleClickFunction}
              orderId={orderId}
              isLincolnBrand={isLincolnBrand}
            />
          </Box>

          <ActionCardsRow
            cards={cards.slice(3)}
            getHandleClickFunction={getHandleClickFunction}
            orderId={orderId}
            isLincolnBrand={isLincolnBrand}
          />
        </Collapse>
      )}

      {cards.length > 3 && (
        <Box sx={{ mt: 2 }}>
          <EMPButton
            testId="second-row-toggle"
            onClick={toggleSecondRow}
            variant="text"
            rightIcon={toggleIcon}
          >
            {toggleText}
          </EMPButton>
        </Box>
      )}
    </>
  );
};

ActionCardGrid.propTypes = {
  handleTabChange: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ActionCardGrid;
